/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable jsx-a11y/control-has-associated-label */
import clsx from 'clsx';
import React, { type KeyboardEvent, useRef, useState } from 'react';
import { Link as WouterLink } from 'wouter';

import type { TextSizeType } from '../utils';
import { sizeToTextClassName } from '../utils';
import type { BaseRowType, BaseTableProps, ColumnDefinition } from './helpers';
import type { LoadMoreRowProps } from './Row';
import { Cell } from './Cell';
import { LoadMoreRow } from './Row';

const formatKeyAsText = <T,>(data: T) => <span>{String(data)}</span>;

const format = <T,>(value: T, definition: ColumnDefinition<T>) => {
  if ('format' in definition) {
    return definition.format(value);
  }
  return formatKeyAsText(value[definition.key]);
};

type RowV2Props<T> = {
  value: T;
  schema: readonly ColumnDefinition<T>[];
  selected?: boolean;
  index: number;
  textSize?: TextSizeType;
  isLast?: boolean;
};

const RowV2 = <T,>({
  value,
  schema,
  selected,
  index,
  textSize = 'sm',
  isLast = false,
}: RowV2Props<T>) => (
  <div
    className={clsx(
      'grid grid-flow-col auto-cols-fr gap-x-[1ch]',
      'px-f1 py-f1',
      sizeToTextClassName[textSize],
      selected ? 'bg-negative' : 'bg-transparent',
      'group-hover:bg-negative group-focus:bg-negative cursor-pointer',
      'border-b-[0.5px] border-main-200'
    )}
    role="row"
    aria-rowindex={index}
  >
    {schema.map((def) => (
      <Cell
        textSize={textSize}
        key={def.title}
        justify={def.justify}
        colspan={def.colspan}
      >
        {format(value, def)}
      </Cell>
    ))}
  </div>
);
RowV2.displayName = 'NavTableV2.Row';

type HeaderV2Props<T extends BaseRowType> = {
  schema: readonly ColumnDefinition<T>[];
  textSize?: TextSizeType;
  showHeader?: boolean;
};

const HeaderV2 = <T extends BaseRowType>({
  schema,
  textSize = 'sm',
  showHeader = true,
}: HeaderV2Props<T>) => {
  if (!showHeader) return null;

  return (
    <div
      className={clsx(
        'grid grid-flow-col auto-cols-fr gap-x-[1ch]',
        'px-f1 py-f1',
        sizeToTextClassName[textSize],
        'border-b-[0.5px] border-main-200'
      )}
      role="row"
    >
      {schema.map(({ justify, colspan, title }) => (
        <div
          key={title}
          className={clsx(
            justify === 'right' ? 'text-right' : 'text-left',
            colspan
          )}
        >
          <div
            className="text-main-500 flex flex-row items-center"
            role="columnheader"
          >
            <span
              className={clsx([
                'flex flex-shrink-0',
                justify === 'right' && 'ml-auto',
              ])}
            >
              {title}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
};
HeaderV2.displayName = 'NavTableV2.Header';

export type KeyboardShortcut<T> = {
  handleKeyPress: (elem: T, e: KeyboardEvent) => void;
};

export type BaseNavTableV2Props<T extends BaseRowType> = BaseTableProps<T> & {
  getPath: (elem: T) => string;
  onRowHover?: (elem: T) => void;
  keyboardShortcuts?: Record<KeyboardEvent['key'], KeyboardShortcut<T>>;
  hasNextPage?: false;
  stickyHeader?: boolean;
  showHeader?: boolean;
};

export type NavTableV2Props<T extends BaseRowType> =
  | BaseNavTableV2Props<T>
  | (Omit<BaseNavTableV2Props<T>, 'hasNextPage'> & {
      hasNextPage: true;
    } & LoadMoreRowProps);

/**
 * A Nav Table V2 with cleaner styling
 * @param data - an array of data
 * @param selected - whether or not the row has been selected
 * @param getPath: - retrieves the href for a rendered row
 * @param schema - the table schema. See `ColumnDefinition`
 * @param emptyValue - a mapping of row title to string. Rendered when no data is present
 * @param showHeader - whether to show header titles (default: true)
 */
export const NavTableV2 = <T extends BaseRowType>(
  props: NavTableV2Props<T>
) => {
  const {
    data,
    selected,
    onRowHover,
    getPath,
    schema,
    keyboardShortcuts,
    emptyValue,
    hasNextPage,
    'data-testid': dataTestId,
    textSize = 'sm',
    stickyHeader = true,
    showHeader = true,
  } = props;

  // In some cases, we will arrive at this table with a member already selected
  // due to deeplinks. If that's the case, pre-set the focus index. Otherwise, set to 0
  const selectedIdx = data.findIndex((row) => row.id === selected?.id);
  const [focusedIdx, setFocusedIdx] = useState<number>(
    selectedIdx < 0 ? 0 : selectedIdx
  );
  const navTableRef = useRef<HTMLDivElement>(null);

  return (
    <div
      className="min-h-f0"
      role="table"
      aria-label="Table"
      data-testid={dataTestId}
      ref={navTableRef}
    >
      {showHeader && (
        <div
          className={
            stickyHeader ? 'sticky -top-f4 pt-f4 -mt-f4' : ''
          }
        >
          <HeaderV2
            textSize={textSize}
            schema={schema}
            showHeader={showHeader}
          />
        </div>
      )}
      {/* the table height should be the left overspace after subtracting padding and other */}
      {/* component heights in the main pane */}
      {data.length > 0 && (
        <div className="overflow-auto h-[calc(100%_-_27px)] z-0 pb-f2">
          {data.map((row, idx) => (
            // href is passed to anchor via wouter link
            <WouterLink href={getPath(row)} key={String(row.id)}>
              <a
                data-testid="nav-table-v2-row"
                className="group outline-none"
                data-idx={String(idx)}
                onMouseOver={onRowHover ? () => onRowHover(row) : undefined}
                onKeyDown={(e: React.KeyboardEvent<HTMLAnchorElement>) => {
                  if (keyboardShortcuts && e.key in keyboardShortcuts) {
                    keyboardShortcuts[e.key].handleKeyPress(row, e);
                  }
                  if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    const index = idx === 0 ? 0 : idx - 1;
                    navTableRef.current
                      ?.querySelector<HTMLAnchorElement>(
                        `a[data-idx="${index}"]`
                      )
                      ?.focus();
                  } else if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    const index = idx === data.length ? data.length : idx + 1;
                    navTableRef.current
                      ?.querySelector<HTMLAnchorElement>(
                        `a[data-idx="${index}"]`
                      )
                      ?.focus();
                  }
                }}
                onFocus={() => setFocusedIdx(idx)}
                tabIndex={idx === focusedIdx ? 0 : -1}
              >
                <RowV2
                  textSize={textSize}
                  index={idx}
                  value={row}
                  selected={selected?.id === row.id}
                  schema={schema}
                  isLast={idx === data.length - 1}
                />
              </a>
            </WouterLink>
          ))}
          {hasNextPage && (
            // can't destructure because props are only present when hasNextPage is true
            <LoadMoreRow
              // eslint-disable-next-line react/destructuring-assignment
              isLoading={props.isLoading}
              // eslint-disable-next-line react/destructuring-assignment
              loadMore={props.loadMore}
              textSize={textSize}
              bordered={false}
            />
          )}
        </div>
      )}
      {/* If no rows, optionally display a sentinel row to avoid a weird UX */}
      {/* Use data.length, not !data for array-ish objects (used in stories) */}
      {data.length === 0 && emptyValue && (
        <div className="overflow-auto h-[calc(100%_-_27px)] z-0 pb-f2">
          <div
            className={clsx(
              'grid grid-flow-col auto-cols-fr gap-x-[1ch]',
              'px-f1 py-f1',
              sizeToTextClassName[textSize],
              'bg-transparent',
              'border-b-[0.5px] border-main-200'
            )}
            role="row"
          >
            {schema.map((def) => (
              <Cell
                textSize={textSize}
                key={def.title}
                justify={def.justify}
                colspan={def.colspan}
              >
                {emptyValue[def.title]}
              </Cell>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
